import { defineStore, acceptHMRUpdate } from 'pinia'
import siteConfig from '@/config.json'
import axios from 'axios'
import { getDateStandard } from '@/helpers/datetime'
import { ticketsRemaining } from '@/helpers/tickets'
import { eventImageUrl } from '@/helpers/images'

import { usDateFormatToYYYYMMDD, timestampToShortDateText, timestampsToDateRange } from '@/helpers/datetime'

const allowedParams = ['startDate', 'endDate', 'eventGroup']
const defaultFreeTicketDisplay = {
  priceText: 'Free',
  buttonText: 'Add To Cart',
}

const getFreeTicketDisplay = () => ({
  ...defaultFreeTicketDisplay,
  ...(siteConfig.features?.events?.freeTicketDisplay || {}),
})

const formatTierCost = (cost, { fixed = false } = {}) => {
  const numericCost = Number(cost)
  if (Number.isNaN(numericCost)) return ''
  if (numericCost === 0) return '$0.00'
  return fixed ? `$${numericCost.toFixed(2)}` : `$${numericCost}`
}

const decorateSession = (session) => {
  session.dateLabel = timestampToShortDateText(session.date)
  session.timeLabel = `${session.startTimeFmt} – ${session.endTimeFmt}`
  return session
}

export const useEventStore = defineStore('EventStore', {
  state: () => {
    return {
      events: [],
      groups: [],
      filters: {}, // must be an array of values
      loading: true,
    }
  },

  getters: {
    filteredEvents: (state) => {
      state.setFiltersFromUrlParams()
      return state.events.filter((event) => {
        // Skip incomplete ACC events (created/published without sessions yet)
        if (!(event.sessions || []).length) {
          return false
        }

        if (siteConfig.features?.hidePastEvents && !(event.futureSessions || []).length) {
          return false
        }

        return Object.keys(state.filters).every((key) => {
          if (allowedParams.includes(key) && Array.isArray(state.filters[key])) {
            return state.filters[key].includes(event[key])
          } else {
            return true
          }
        })
      })
    },
  },

  actions: {
    setFiltersFromUrlParams() {
      const params = new URLSearchParams(window.location.search)

      this.filters = Array.from(params.entries()).reduce((filters, [key, value]) => {
        if (!value) {
          return filters
        }
        filters[key] = value.split(',')
        return filters
      }, {})
    },
    setParamsFromFilters() {
      // Start with existing URL parameters
      const searchParams = new URLSearchParams(window.location.search)

      // Remove all existing filter parameters
      Object.keys(this.filters).forEach((key) => {
        searchParams.delete(key)
      })

      // Add current filter parameters
      Object.entries(this.filters).forEach(([key, values]) => {
        if (values?.length) {
          searchParams.set(key, values.join(','))
        }
      })

      window.location.search = searchParams.toString()
    },
    setFilter(key, value) {
      if (!value) {
        this.filters[key] = null
      } else {
        this.filters[key] = [value]
      }
      this.setParamsFromFilters()
    },
    formatEvent(event) {
      const freeTicketDisplay = getFreeTicketDisplay()

      event.imageUrl = eventImageUrl(event, siteConfig)
      event.name = event.teventName
      event.id = event.teventId
      // Incomplete ACC events can land with null/empty sessions or ticketMap.
      // Guard so one bad event cannot wipe the whole public listing.
      event.sessions = Array.isArray(event.sessions) ? event.sessions : []
      event.sessions.sort((a, b) => a.date - b.date)
      event.ticketMap = event.ticketMap && typeof event.ticketMap === 'object' ? event.ticketMap : {}

      if (event.sessions.length) {
        event.startDateStandard = event.sessions[0].date
        event.endDateStandard = event.sessions[event.sessions.length - 1].date
        event.dateRangeStandard = timestampsToDateRange(
          event.sessions[0].date,
          event.sessions[event.sessions.length - 1].date
        )
        // Check if all sessions have same start/end time
        const allSameTime = event.sessions.every(
          (session) =>
            session.startTimeFmt === event.sessions[0].startTimeFmt &&
            session.endTimeFmt === event.sessions[0].endTimeFmt
        )
        event.timeLabel = allSameTime ? `${event.startTime} – ${event.endTime}` : 'Various times'
      } else {
        event.startDateStandard = null
        event.endDateStandard = null
        event.dateRangeStandard = ''
        event.timeLabel = ''
      }

      event.tiers = []
      event.costLow = null
      event.costHigh = null

      event.ticketIdForScheduleId = {}
      Object.keys(event.ticketMap).forEach((ticketId) => {
        event.ticketIdForScheduleId[event.ticketMap[ticketId]] = parseInt(ticketId)
      })
      for (let i = 1; i <= 4; i++) {
        if (event[`publishPrice${i}`]) {
          let cost = event[`cost${i}`]
          if (event.costLow === null) {
            event.costLow = cost
          } else if (cost < event.costLow) {
            event.costLow = cost
          }

          if (event.costHigh === null) {
            event.costHigh = cost
          } else if (cost > event.costHigh) {
            event.costHigh = cost
          }
          event.tiers.push({
            name: event[`prname${i}`],
            cost: cost,
            displayCost: cost === 0 ? freeTicketDisplay.priceText : formatTierCost(cost),
            buttonText: cost === 0 ? freeTicketDisplay.buttonText : defaultFreeTicketDisplay.buttonText,
            isFree: cost === 0,
          })
        }
      }
      event.hasFreeTicket = event.tiers.some((tier) => tier.isFree)
      if (event.tiers.length === 0) {
        event.costSummary = ''
      } else if (event.costLow === event.costHigh) {
        event.costSummary = event.costLow === 0 ? freeTicketDisplay.priceText : formatTierCost(event.costLow)
      } else {
        const lowCostLabel =
          event.costLow === 0 ? freeTicketDisplay.priceText : formatTierCost(event.costLow, { fixed: true })
        event.costSummary = `${lowCostLabel} - ${formatTierCost(event.costHigh, { fixed: true })}`
      }

      let todaysDateStandard = getDateStandard()

      event.futureSessions = (event.sessions || []).filter((session) => {
        const sessionDateStandard = getDateStandard(session.date)
        if (todaysDateStandard > sessionDateStandard) return false

        return true
      })
      event.futureSessions = event.futureSessions.sort((a, b) => a.date - b.date)
      event.futureSessions.forEach((session) => decorateSession(session))

      event.upcomingSessions = event.futureSessions.filter((session) => ticketsRemaining(event, session.scheduleId) > 0)
      return event
    },

    fetchEvents() {
      if (this.events.length) {
        return
      }

      this.loading = true

      axios
        .get(`${siteConfig.apiBaseUrl}/tickets/allevents`)
        .then((response) => {
          const results = Array.isArray(response.data.results) ? response.data.results : []
          let events = results
            .map((event) => {
              try {
                return this.formatEvent(event)
              } catch (err) {
                console.error('Failed to format event', event?.teventId, err)
                return null
              }
            })
            .filter(Boolean)

          this.events = events.sort((a, b) => {
            if (a.displayWeight !== b.displayWeight) {
              return a.displayWeight - b.displayWeight
            }
            return (a.startDateStandard || 0) - (b.startDateStandard || 0)
          })

          this.groups = [...new Set(this.events.map((evt) => evt.eventGroup).filter((grp) => !!grp))]
        })
        .catch((err) => {
          console.error('Failed to fetch events', err)
          this.events = []
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchEventById(id) {
      return axios.get(`${siteConfig.apiBaseUrl}/tickets/event/${id}`).then((response) => {
        const event = response.data.results
        // a missing event comes back as ok with results: 'Oh Darn'
        if (!event?.teventId) return null
        return this.formatEvent(event)
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot))
}
